/**
 * Tiny sample-level DSP toolkit used to render procedural buffers once at
 * start-up (noise beds, grains, modal impacts, impulse responses). Pure JS,
 * no Web Audio dependency, deterministic for a given seed.
 */
export type Rng = () => number;

export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const range = (rng: Rng, lo: number, hi: number) => lo + (hi - lo) * rng();
export const pick = <T>(rng: Rng, items: readonly T[]): T => items[Math.floor(rng() * items.length) % items.length];

export function rms(data: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
  return Math.sqrt(sum / Math.max(1, data.length));
}
export function peak(...channels: Float32Array[]): number {
  let p = 0;
  for (const data of channels) for (let i = 0; i < data.length; i++) { const v = Math.abs(data[i]); if (v > p) p = v; }
  return p;
}
/** Scale channels so the loudest sample hits `target`. Silent input is left alone. */
export function normalizePeak(channels: Float32Array[], target: number): Float32Array[] {
  const p = peak(...channels);
  if (p > 1e-9) for (const data of channels) for (let i = 0; i < data.length; i++) data[i] *= target / p;
  return channels;
}
/** Scale to an RMS target, then keep the peak below `ceiling`. */
export function normalizeRms(data: Float32Array, target: number, ceiling = .98): Float32Array {
  const r = rms(data);
  if (r > 1e-9) { const g = target / r; for (let i = 0; i < data.length; i++) data[i] *= g; }
  const p = peak(data);
  if (p > ceiling) for (let i = 0; i < data.length; i++) data[i] *= ceiling / p;
  return data;
}

export function whiteNoise(n: number, rng: Rng): Float32Array {
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) out[i] = rng() * 2 - 1;
  return out;
}
/** Paul Kellet's refined pink filter. */
export function pinkNoise(n: number, rng: Rng): Float32Array {
  const out = new Float32Array(n);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < n; i++) {
    const w = rng() * 2 - 1;
    b0 = .99886 * b0 + w * .0555179; b1 = .99332 * b1 + w * .0750759; b2 = .969 * b2 + w * .153852;
    b3 = .8665 * b3 + w * .3104856; b4 = .55 * b4 + w * .5329522; b5 = -.7616 * b5 - w * .016898;
    out[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * .5362; b6 = w * .115926;
  }
  return out;
}
/** Leaky-integrated (red/brown) noise. */
export function brownNoise(n: number, rng: Rng): Float32Array {
  const out = new Float32Array(n);
  let y = 0;
  for (let i = 0; i < n; i++) { y = (y + .02 * (rng() * 2 - 1)) * .998; out[i] = y; }
  return out;
}
/** Make a noise loop seamless by crossfading the tail into the head. */
export function seamless(data: Float32Array, fade: number): Float32Array {
  const n = data.length, f = Math.min(fade, Math.floor(n / 4));
  for (let i = 0; i < f; i++) {
    const t = i / f, a = Math.sqrt(t), b = Math.sqrt(1 - t);
    data[i] = data[i] * a + data[n - f + i] * b;
  }
  return data.subarray(0, n - f).slice();
}

/** RBJ biquad in transposed direct form II, for offline rendering. */
export class Biquad {
  private b0 = 1; private b1 = 0; private b2 = 0; private a1 = 0; private a2 = 0; private z1 = 0; private z2 = 0;
  constructor(type: 'lowpass' | 'highpass' | 'bandpass', freq: number, q: number, sampleRate: number) { this.set(type, freq, q, sampleRate); }
  set(type: 'lowpass' | 'highpass' | 'bandpass', freq: number, q: number, sampleRate: number) {
    const w = 2 * Math.PI * Math.min(freq, sampleRate * .45) / sampleRate, cos = Math.cos(w), alpha = Math.sin(w) / (2 * Math.max(.05, q));
    const a0 = 1 + alpha;
    if (type === 'lowpass') { this.b0 = (1 - cos) / 2 / a0; this.b1 = (1 - cos) / a0; this.b2 = this.b0; }
    else if (type === 'highpass') { this.b0 = (1 + cos) / 2 / a0; this.b1 = -(1 + cos) / a0; this.b2 = this.b0; }
    else { this.b0 = alpha / a0; this.b1 = 0; this.b2 = -alpha / a0; }
    this.a1 = -2 * cos / a0; this.a2 = (1 - alpha) / a0;
    return this;
  }
  process(x: number): number {
    const y = this.b0 * x + this.z1;
    this.z1 = this.b1 * x - this.a1 * y + this.z2;
    this.z2 = this.b2 * x - this.a2 * y;
    return y;
  }
  reset() { this.z1 = this.z2 = 0; return this; }
}
/** One-pole lowpass coefficient for a cutoff. */
export const onePole = (freq: number, sampleRate: number) => 1 - Math.exp(-2 * Math.PI * freq / sampleRate);

/**
 * Add an exponentially decaying sinusoid (a single vibration mode) using a
 * rotation recurrence: cheap, no Math.sin per sample. `tau` is the 1/e time.
 */
export function addMode(out: Float32Array, start: number, freq: number, amp: number, tau: number, sampleRate: number, attack = .0006) {
  if (freq <= 0 || freq >= sampleRate / 2 || amp === 0) return;
  const w = 2 * Math.PI * freq / sampleRate, c = Math.cos(w), s = Math.sin(w);
  const r = Math.exp(-1 / (tau * sampleRate));
  const attackSamples = Math.max(1, attack * sampleRate);
  let re = 0, im = amp, env = 1;
  const end = Math.min(out.length, start + Math.ceil(tau * sampleRate * 7.5));
  for (let i = Math.max(0, start), k = i - start; i < end; i++, k++) {
    const a = k < attackSamples ? k / attackSamples : 1;
    out[i] += im * a;
    const nr = re * c - im * s, ni = re * s + im * c; re = nr * r; im = ni * r; env *= r;
    if (env < 1e-5) break;
  }
}
/** Add a filtered noise burst with an exponential decay and short attack. */
export function addBurst(out: Float32Array, start: number, amp: number, tau: number, rng: Rng, filter?: Biquad, attack = .0004, sampleRate = 48000) {
  const len = Math.ceil(tau * sampleRate * 7), attackSamples = Math.max(1, attack * sampleRate);
  filter?.reset();
  for (let k = 0; k < len; k++) {
    const i = start + k; if (i >= out.length) break; if (i < 0) continue;
    const env = Math.exp(-k / (tau * sampleRate)) * (k < attackSamples ? k / attackSamples : 1);
    const x = (rng() * 2 - 1) * env;
    out[i] += amp * (filter ? filter.process(x) : x);
  }
}

/** Seamless looping smooth random signal in [-1, 1] through `points` control points (Catmull-Rom). */
export function smoothRandom(n: number, points: number, rng: Rng): Float32Array {
  const p = Math.max(4, Math.floor(points));
  const ctrl = Array.from({ length: p }, () => rng() * 2 - 1);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = i / n * p, k = Math.floor(x), t = x - k;
    const p0 = ctrl[(k - 1 + p) % p], p1 = ctrl[k % p], p2 = ctrl[(k + 1) % p], p3 = ctrl[(k + 2) % p];
    out[i] = .5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t + (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t);
  }
  return normalizePeak([out], 1)[0];
}

/**
 * Fourier coefficients of a periodic envelope for a PeriodicWave with
 * `disableNormalization`. `mean` must be added as the base of the gain the
 * oscillator modulates so the envelope stays non-negative.
 */
export function fourierSeries(shape: (phase: number) => number, harmonics: number, samples = 2048) {
  const values = new Float32Array(samples);
  let mean = 0;
  for (let i = 0; i < samples; i++) { values[i] = shape(i / samples); mean += values[i]; }
  mean /= samples;
  const real = new Float32Array(harmonics + 1), imag = new Float32Array(harmonics + 1);
  for (let k = 1; k <= harmonics; k++) {
    let a = 0, b = 0;
    for (let i = 0; i < samples; i++) { const w = 2 * Math.PI * k * i / samples; a += values[i] * Math.cos(w); b += values[i] * Math.sin(w); }
    real[k] = 2 * a / samples; imag[k] = 2 * b / samples;
  }
  return { real, imag, mean };
}
/** Band-limited impulse train coefficients (cosine series). `tilt` > 0 darkens upper harmonics. */
export function impulseTrain(harmonics: number, tilt = 0) {
  const real = new Float32Array(harmonics + 1), imag = new Float32Array(harmonics + 1);
  for (let k = 1; k <= harmonics; k++) real[k] = 1 / (1 + tilt * (k - 1));
  return { real, imag };
}

/** PolyBLEP sawtooth oscillator, for rendering voices and brass. */
export class Saw {
  private phase: number;
  constructor(phase = 0) { this.phase = phase; }
  next(freq: number, sampleRate: number): number {
    const dt = Math.min(.5, freq / sampleRate);
    this.phase += dt; if (this.phase >= 1) this.phase -= 1;
    const t = this.phase;
    let y = 2 * t - 1;
    if (t < dt) { const x = t / dt; y -= x + x - x * x - 1; }
    else if (t > 1 - dt) { const x = (t - 1) / dt; y -= x * x + x + x + 1; }
    return y;
  }
}
